import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import VisitDetailsFields, { EMPTY_VISIT, toVisitPayload } from './VisitDetailsFields';
import AttachmentField from './AttachmentField';
import { patientApi, settingsApi, visitApi } from '../../api';
import { useToast } from '../../context/ToastContext';
import { formatAge, titleCase } from '../../utils/format';

/**
 * Reception's "send the patient in" step for someone already on the books.
 * A brand new patient is queued straight from the registration form instead,
 * so this dialog never appears there.
 */
export default function StartVisitModal({ show, patient, onHide, onDone }) {
  const toast = useToast();
  const [visit, setVisit] = useState(EMPTY_VISIT);
  // Anything brought along today. Existing reports already sit on the record.
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !patient) return;

    setVisit({ ...EMPTY_VISIT, weight: patient.weight ?? '' });
    setReports([]);
    setError('');

    // Pre-fill the fee the clinic has configured, so the desk just confirms it.
    settingsApi
      .get()
      .then((res) => {
        const isRevisit = (patient.totalVisits || 0) > 0;
        const amount = String((isRevisit ? res.data.followUpFee : res.data.defaultConsultationFee) ?? 0);
        setVisit((v) => ({ ...v, fee: amount, paid: amount }));
      })
      .catch(() => setVisit((v) => ({ ...v, fee: '0', paid: '0' })));
  }, [show, patient]);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      // Files go onto the patient record first, so the doctor sees them even if
      // something goes wrong with the queue. Clearing the list stops a retry
      // attaching the same report twice.
      if (reports.length) {
        await patientApi.addReport(patient._id, { reports });
        setReports([]);
      }

      const res = await visitApi.create({ patient: patient._id, ...toVisitPayload(visit) });

      toast.success(`${patient.name} added to the queue on token ${res.data.tokenNo}`);
      onDone?.(res.data);
      onHide();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (!patient) return null;

  const isRevisit = (patient.totalVisits || 0) > 0;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            <i className="bi bi-clipboard2-plus me-2" style={{ color: 'var(--clinic-primary)' }} />
            Send to Doctor
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="bg-light rounded p-3 mb-3 d-flex flex-wrap justify-content-between gap-2">
            <div>
              <div className="fw-bold fs-5">{patient.name}</div>
              <div className="text-muted small">
                {titleCase(patient.gender)}, {formatAge(patient)} &middot;{' '}
                <i className="bi bi-telephone" /> {patient.mobile}
              </div>
            </div>
            <div className="text-end">
              <span className={`badge ${isRevisit ? 'bg-info' : 'bg-success'}`}>
                {isRevisit ? `Revisit (${patient.totalVisits} before)` : 'First visit'}
              </span>
            </div>
          </div>

          {patient.allergies?.length > 0 && (
            <div className="allergy-alert mb-3">
              <i className="bi bi-exclamation-triangle-fill me-2" />
              Allergic to: <strong>{patient.allergies.join(', ')}</strong>
            </div>
          )}

          {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

          <VisitDetailsFields value={visit} onChange={setVisit} />

          <div className="fw-semibold small text-uppercase text-muted mt-3 mb-2">
            Reports brought today
            <span className="text-muted fw-normal text-lowercase ms-2">
              the doctor opens these during the consultation
            </span>
          </div>
          <AttachmentField compact value={reports} onChange={setReports} disabled={busy} />
        </Modal.Body>

        <Modal.Footer className="justify-content-between">
          <span className="text-muted small d-none d-sm-inline">
            <i className="bi bi-info-circle me-1" />
            The doctor sees this patient on their queue straight away.
          </span>
          <div className="d-flex gap-2">
            <Button variant="light" onClick={onHide} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" size="lg" className="btn-clinic px-4" disabled={busy}>
              {busy ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-send me-2" />}
              Add to Queue
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
